/**
 * PhoneInput - International phone number input with country code selector.
 * Uses react-international-phone's usePhoneInput hook for phone state,
 * Headless UI Listbox (styled like shared-ui Select) for country picker,
 * google-libphonenumber for validation, and shared-ui Input for the text field.
 * Returns E.164 format string for storage.
 */
import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import {
  usePhoneInput,
  defaultCountries,
  parseCountry,
  FlagImage,
} from 'react-international-phone';
import { PhoneNumberUtil } from 'google-libphonenumber';
import type { CustomFieldValidation } from '@revont/shared-types';
import { Input } from './Input';
import { FieldLabel } from './FieldLabel';
import 'react-international-phone/style.css';

const phoneUtil = PhoneNumberUtil.getInstance();

function isValidPhone(phone: string): boolean {
  if (!phone || phone.length < 4) return false;
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch {
    return false;
  }
}

export interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  /** Help text shown as a `?` tooltip beside the label. */
  helpText?: string;
  error?: string;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  /**
   * When true, the label shows a required asterisk and the field is marked
   * as required for form submission. Note: format validation (valid phone
   * number, min/max length, pattern) always runs on blur when there is a
   * non-empty value, regardless of this flag. Previously, format validation
   * only ran when `required` was true; this changed to ensure optional fields
   * with a value are still validated for correctness.
   */
  required?: boolean;
  placeholder?: string;
  className?: string;
  validation?: CustomFieldValidation;
}

const sizeClasses = {
  small: 'h-8 px-3 text-xs',
  medium: 'h-10 px-4 text-sm',
  large: 'h-12 px-5 text-base',
};

interface CountryOption {
  iso2: string;
  name: string;
}

export function PhoneInput({
  value = '',
  onChange,
  label,
  helpText,
  error: externalError,
  disabled = false,
  size = 'medium',
  required = false,
  placeholder = 'Enter phone number',
  className = '',
  validation,
}: PhoneInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isOpenRef = useRef(false);
  const [listboxKey, setListboxKey] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);

  const { inputValue, phone, country, setCountry, handlePhoneValueChange } = usePhoneInput({
    defaultCountry: 'us',
    value,
    onChange: data => {
      setValidationError(null);
      onChange?.(data.phone);
    },
    inputRef,
  });

  // Close listbox on page-focus (matches Select behavior for Activity resume)
  useEffect(() => {
    const handler = () => setListboxKey(k => k + 1);
    window.addEventListener('page-focus', handler);
    return () => window.removeEventListener('page-focus', handler);
  }, []);

  // Build country options (no dial code - just flag + name)
  const countryOptions: CountryOption[] = useMemo(() => {
    return defaultCountries.map(c => {
      const parsed = parseCountry(c);
      return {
        iso2: parsed.iso2,
        name: parsed.name,
      };
    });
  }, []);

  const selectedOption = countryOptions.find(o => o.iso2 === country.iso2) ?? countryOptions[0];

  const handleBlur = useCallback(() => {
    if (phone && !isValidPhone(phone)) {
      setValidationError('Please enter a valid phone number');
    } else if (
      phone &&
      validation?.minLength !== undefined &&
      phone.length < validation.minLength
    ) {
      setValidationError(`Must be at least ${validation.minLength} characters`);
    } else if (
      phone &&
      validation?.maxLength !== undefined &&
      phone.length > validation.maxLength
    ) {
      setValidationError(`Must be at most ${validation.maxLength} characters`);
    } else if (phone && validation?.pattern) {
      try {
        setValidationError(
          new RegExp(validation.pattern).test(phone)
            ? null
            : validation.patternMessage || 'Phone number does not match the required format'
        );
      } catch {
        setValidationError('The configured phone validation pattern is invalid');
      }
    } else {
      setValidationError(null);
    }
  }, [phone, validation]);

  const displayError = externalError || validationError;

  return (
    <div
      className={`relative ${className}`}
      style={
        {
          '--react-international-phone-flag-width': '20px',
          '--react-international-phone-flag-height': '20px',
        } as React.CSSProperties
      }
    >
      {label && (
        <FieldLabel required={required} helpText={helpText}>
          {label}
        </FieldLabel>
      )}

      <Listbox
        key={listboxKey}
        value={country.iso2}
        onChange={iso2 => setCountry(iso2, { focusOnInput: true })}
        disabled={disabled}
      >
        {({ open }) => {
          isOpenRef.current = open;
          return (
            <div className="flex items-stretch gap-0">
              {/* Country selector button - styled like shared-ui Select */}
              <ListboxButton
                className={`
                  group relative flex items-center gap-1.5 px-2
                  ${sizeClasses[size]
                    .split(' ')
                    .filter(c => c.startsWith('h-'))
                    .join(' ')}
                  rounded-l-lg rounded-r-none
                  bg-white/5 backdrop-blur-xl
                  text-white/80 font-normal
                  transition-all duration-300
                  focus:outline-none
                  focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
                  disabled:opacity-50 disabled:cursor-not-allowed
                  motion-reduce:transition-none
                  ${open && !displayError ? 'bg-white/8' : ''}
                  ${displayError ? 'shadow-[0_0_20px_rgb(239_68_68_/_0.2)]' : ''}
                  ${!open && !displayError ? 'hover:bg-white/8' : ''}
                `}
              >
                <FlagImage iso2={selectedOption.iso2} size="20px" />
              </ListboxButton>

              {/* Phone input - using shared-ui Input with onRawChange for handlePhoneValueChange */}
              <Input
                ref={inputRef}
                type="tel"
                value={inputValue}
                onRawChange={handlePhoneValueChange}
                onBlur={handleBlur}
                disabled={disabled}
                placeholder={placeholder}
                aria-label={label || 'Phone number'}
                error={displayError ?? undefined}
                noLeftRounding
                size={size}
                className="flex-1"
              />

              {/* Dropdown options */}
              <ListboxOptions
                anchor="bottom start"
                transition
                className={`
                  z-[10050] min-w-[var(--button-width)] w-max max-w-xs [--anchor-max-height:240px]
                  glass-surface rounded-xl
                  overflow-auto [scrollbar-width:none]! [-ms-overflow-style:none]! [&::-webkit-scrollbar]:hidden! [&::-webkit-scrollbar]:[width:0]! [&::-webkit-scrollbar]:[height:0]!
                  py-1
                  focus:outline-none
                  transition duration-200 ease-out
                  data-[closed]:opacity-0 data-[closed]:translate-y-1 data-[closed]:scale-95
                `}
              >
                {countryOptions.map(opt => (
                  <ListboxOption
                    key={opt.iso2}
                    value={opt.iso2}
                    className={({ focus, selected }) => `
                      relative cursor-pointer select-none py-2 px-3 text-sm
                      transition-all duration-150
                      ${focus ? 'bg-primary/15 text-white shadow-[inset_0_0_15px_rgb(59_130_246_/_0.1)]' : 'text-white/90'}
                      ${selected ? 'bg-primary/10 font-medium text-primary' : 'font-normal'}
                    `}
                  >
                    {({ selected }) => (
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <FlagImage iso2={opt.iso2} size="20px" />
                          <span className="flex-1 truncate">{opt.name}</span>
                        </div>
                        {selected && (
                          <svg
                            className="h-4 w-4 text-primary flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    )}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </div>
          );
        }}
      </Listbox>
    </div>
  );
}

PhoneInput.displayName = 'PhoneInput';
